import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Mail, RotateCcw, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  EMAIL_CATEGORIES,
  EMAIL_TEMPLATE_PLACEHOLDERS,
  EXAMPLE_VARS,
  renderTemplate,
  type EmailCategory,
} from "@/lib/email-template";

interface EmailTemplateRow {
  id: string;
  tenant_id: string;
  category: EmailCategory;
  subject: string;
  body_text: string;
  updated_by_email: string | null;
  updated_at: string;
}

interface EmailSendLogRow {
  id: string;
  created_at: string;
  initiator: string;
  category: string;
  to_addresses: string[];
  from_address: string;
  subject: string;
  status: "sent" | "failed" | "skipped";
  error: string | null;
  template_source: "default" | "tenant" | null;
  ses_message_id: string | null;
  related_order_id: string | null;
}

export function EmailerTab() {
  const queryClient = useQueryClient();
  const { tenantId, activeTenant, tenants } = useTenant();
  const { user } = useAuth();
  const [category, setCategory] = useState<EmailCategory>("order_in_transit");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const [lastFocused, setLastFocused] = useState<"subject" | "body">("body");

  const tenantName = tenants.find((t) => t.slug === activeTenant)?.name ?? activeTenant;
  const categoryMeta = EMAIL_CATEGORIES.find((c) => c.value === category)!;

  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates" as never)
        .select("*")
        .eq("tenant_id", tenantId!);
      if (error) throw error;
      return (data ?? []) as unknown as EmailTemplateRow[];
    },
    enabled: !!tenantId,
  });

  const current = useMemo(
    () => templates?.find((t) => t.category === category),
    [templates, category],
  );

  // Sync editor with the selected category whenever the row or category changes.
  useEffect(() => {
    if (current) {
      setSubject(current.subject);
      setBody(current.body_text);
    } else {
      setSubject(categoryMeta.defaultSubject);
      setBody(categoryMeta.defaultBody);
    }
  }, [current, category, categoryMeta.defaultSubject, categoryMeta.defaultBody]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId) throw new Error("No tenant selected");
      const { error } = await supabase
        .from("email_templates" as never)
        .upsert(
          {
            tenant_id: tenantId,
            category,
            subject: subject.trim(),
            body_text: body,
            updated_by: user?.id ?? null,
            updated_by_email: user?.email ?? null,
          } as never,
          { onConflict: "tenant_id,category" } as never,
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates", tenantId] });
      toast.success("Template saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!tenantId || !current) return;
      const { error } = await supabase
        .from("email_templates" as never)
        .delete()
        .eq("tenant_id", tenantId)
        .eq("category", category);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates", tenantId] });
      toast.success("Reverted to default template");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Send-test: routes through send-email's `direct` branch with the
  // currently-edited (unsaved) template rendered against EXAMPLE_VARS, to
  // the signed-in admin's own email. Prefixing the category with `test_`
  // keeps test sends from polluting analytics on the real categories.
  const sendTestMutation = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error("Your account has no email address on file");
      const sub = renderTemplate(subject, EXAMPLE_VARS).output;
      const txt = renderTemplate(body, EXAMPLE_VARS).output;
      if (!sub.trim() || !txt.trim()) throw new Error("Subject and body must not be empty");
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "direct",
          to: [user.email],
          subject: `[TEST] ${sub}`,
          text: txt,
          category: `test_${category}`,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success(`Test email sent to ${user?.email}`);
      queryClient.invalidateQueries({ queryKey: ["email-send-log", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message || "Send test failed"),
  });

  const insertToken = (token: string) => {
    const placeholder = `{${token}}`;
    if (lastFocused === "subject") {
      const el = subjectRef.current;
      const start = el?.selectionStart ?? subject.length;
      const end = el?.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + placeholder + subject.slice(end);
      setSubject(next);
      requestAnimationFrame(() => {
        el?.focus();
        const pos = start + placeholder.length;
        el?.setSelectionRange(pos, pos);
      });
    } else {
      const el = bodyRef.current;
      const start = el?.selectionStart ?? body.length;
      const end = el?.selectionEnd ?? body.length;
      const next = body.slice(0, start) + placeholder + body.slice(end);
      setBody(next);
      requestAnimationFrame(() => {
        el?.focus();
        const pos = start + placeholder.length;
        el?.setSelectionRange(pos, pos);
      });
    }
  };

  const subjectPreview = renderTemplate(subject, EXAMPLE_VARS);
  const bodyPreview = renderTemplate(body, EXAMPLE_VARS);
  const allUnknown = Array.from(
    new Set([...subjectPreview.unknownTokens, ...bodyPreview.unknownTokens]),
  );

  const isDirty = current
    ? subject !== current.subject || body !== current.body_text
    : subject !== categoryMeta.defaultSubject || body !== categoryMeta.defaultBody;

  return (
    <div className="space-y-4">
      <Alert>
        <Mail className="h-4 w-4" />
        <AlertDescription>
          Customize the subject and body of automated order-status emails sent for{" "}
          <strong>{tenantName}</strong>. Use <code className="font-mono">{"{token}"}</code> placeholders
          for dynamic fields. Saved templates override the built-in defaults.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Templates
          </CardTitle>
          <CardDescription>
            Choose a category, edit the template, and save. Click a placeholder to insert it at your cursor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={category} onValueChange={(v) => setCategory(v as EmailCategory)}>
            <TabsList>
              {EMAIL_CATEGORIES.map((c) => (
                <TabsTrigger key={c.value} value={c.value}>
                  {c.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {EMAIL_CATEGORIES.map((c) => (
              <TabsContent key={c.value} value={c.value} className="mt-4">
                {isLoading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email-subject">Subject</Label>
                        <Input
                          id="email-subject"
                          ref={subjectRef}
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          onFocus={() => setLastFocused("subject")}
                          placeholder="Subject line"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email-body">Body</Label>
                        <Textarea
                          id="email-body"
                          ref={bodyRef}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          onFocus={() => setLastFocused("body")}
                          className="font-mono text-sm min-h-[280px]"
                          placeholder="Body text. Use {token} for dynamic fields."
                        />
                      </div>

                      {allUnknown.length > 0 && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Unknown placeholder{allUnknown.length === 1 ? "" : "s"}:{" "}
                            {allUnknown.map((t) => `{${t}}`).join(", ")}. These will be sent as-is.
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase">
                          Preview (with sample data)
                        </div>
                        <div className="text-sm font-semibold">{subjectPreview.output || "—"}</div>
                        <div className="text-sm whitespace-pre-wrap text-muted-foreground">
                          {bodyPreview.output || "—"}
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-2">
                        <div className="text-xs text-muted-foreground">
                          {current
                            ? `Last updated by ${current.updated_by_email ?? "—"} on ${new Date(current.updated_at).toLocaleString()}`
                            : "Using built-in default (no custom template saved)."}
                        </div>
                        <div className="flex gap-2">
                          {current && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resetMutation.mutate()}
                              disabled={resetMutation.isPending}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Revert to default
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => sendTestMutation.mutate()}
                            disabled={
                              !subject.trim() ||
                              !body.trim() ||
                              !user?.email ||
                              sendTestMutation.isPending
                            }
                            title={user?.email ? `Send to ${user.email}` : "No email on your account"}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            {sendTestMutation.isPending ? "Sending…" : "Send test"}
                          </Button>
                          <Button
                            onClick={() => saveMutation.mutate()}
                            disabled={!isDirty || !subject.trim() || !body.trim() || saveMutation.isPending}
                          >
                            {saveMutation.isPending ? "Saving…" : "Save template"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Available placeholders</Label>
                      <div className="rounded-lg border bg-card p-2 space-y-1 max-h-[480px] overflow-y-auto">
                        {EMAIL_TEMPLATE_PLACEHOLDERS.map((p) => (
                          <button
                            key={p.token}
                            type="button"
                            onClick={() => insertToken(p.token)}
                            className="w-full text-left px-2 py-1.5 rounded hover:bg-accent transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <Badge variant="secondary" className="font-mono text-xs">
                                {`{${p.token}}`}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{p.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <SentLogCard tenantId={tenantId} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sent log viewer — read-only window into email_send_log for the current
// tenant. Backed by the existing "Tenant members can read email log" RLS
// policy, so admins/dispatchers/terminal_staff already see their rows.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: Array<{ value: "all" | "sent" | "failed" | "skipped"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

function statusBadgeVariant(status: EmailSendLogRow["status"]): "default" | "destructive" | "secondary" {
  if (status === "sent") return "default";
  if (status === "failed") return "destructive";
  return "secondary";
}

function SentLogCard({ tenantId }: { tenantId: string | undefined }) {
  const [statusFilter, setStatusFilter] = useState<"all" | "sent" | "failed" | "skipped">("all");

  const { data: rows, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["email-send-log", tenantId, statusFilter],
    queryFn: async () => {
      if (!tenantId) return [];
      let q = supabase
        .from("email_send_log" as never)
        .select(
          "id, created_at, initiator, category, to_addresses, from_address, subject, status, error, template_source, ses_message_id, related_order_id",
        )
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(100);
      if (statusFilter !== "all") {
        q = q.eq("status", statusFilter);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as EmailSendLogRow[];
    },
    enabled: !!tenantId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Sent log
        </CardTitle>
        <CardDescription>
          Last 100 customer email events for this tenant — sends, skips, and failures.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !rows || rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No email events match this filter.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium">To</th>
                  <th className="px-3 py-2 font-medium">Subject</th>
                  <th className="px-3 py-2 font-medium">Initiator</th>
                  <th className="px-3 py-2 font-medium">Template</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusBadgeVariant(r.status)}>{r.status}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.category}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.to_addresses?.length ? r.to_addresses.join(", ") : "—"}
                    </td>
                    <td className="px-3 py-2 max-w-[280px]">
                      <div className="truncate" title={r.subject || r.error || ""}>
                        {r.subject || "—"}
                      </div>
                      {r.status !== "sent" && r.error && (
                        <div className="text-xs text-destructive truncate" title={r.error}>
                          {r.error}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.initiator}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.template_source ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}